// File: src/utils/revealEnforcer.js
import axios from "axios";

/* ───────────────────────── Auth header ───────────────────────── */
function authHeaders() {
  const token =
    localStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token ||
    "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

/* ───────────────────────── In-memory bus ─────────────────────────
   The enforcer publishes the latest server snapshot here so all
   pages can subscribe without refactoring their data flows. */
const _mem = new Map();            // eventId -> last snapshot
const _subs = new Map();           // eventId -> Set<callback>

export function getRevealSnapshot(eventId) {
  return _mem.get(String(eventId)) || null;
}

export function subscribeRevealSnapshot(eventId, cb) {
  const key = String(eventId);
  if (!_subs.has(key)) _subs.set(key, new Set());
  _subs.get(key).add(cb);
  return () => _subs.get(key)?.delete(cb);
}

function _publish(eventId, snap) {
  const key = String(eventId);
  _mem.set(key, snap);

  const set = _subs.get(key);
  if (set) for (const fn of set) { try { fn(snap); } catch {} }

  // Also fire a DOM event so legacy pages can listen without importing.
  try {
    window.dispatchEvent(
      new CustomEvent("reveal:snapshot", { detail: { eventId: key, snap } })
    );
  } catch {}
}

/* ───────────────────────── Helpers ───────────────────────── */
const routeLockKey = (eventId) => `reveal_route_lock:${eventId}`;

/** Optional helper you can call from Partner to persist "found" to the server. */
export async function markFoundPartner(api, matchId) {
  if (!api || !matchId) return;
  try { await axios.post(`${api}/matches/${matchId}/found-partner`, {}, authHeaders()); } catch {}
}

/* Map server state.kind → desired client route */
function routeFromKind(kind) {
  switch (kind) {
    case "match":         return "partner";
    case "ended":         return "portal";
    case "computing":
    case "r1-waiting":
    case "s2-pending":
    case "inmatch-done":
    case "error":
    default:              return "waiting"; // render-in-place
  }
}

/* ───────────────────────── Enforcer ─────────────────────────
   pageKind: 'portal' | 'partner' | 'verify' | 'inmatch'

   Responsibilities:
   1) Fetch *authoritative* snapshot from server
   2) Publish it to subscribers (and DOM event)
   3) Navigate if the authoritative route differs from current
   4) Respect a short route lock to avoid rubber-banding
*/
export async function enforceRevealState({ api, eventId, pageKind, navigate }) {
  const now = Date.now();
  const key = String(eventId);
  const ROUTE_LOCK_KEY = routeLockKey(key);

  // 🔒 If we *just* navigated, ignore flips briefly to prevent bounce.
  try {
    const lockUntil = Number(sessionStorage.getItem(ROUTE_LOCK_KEY) || 0);
    if (lockUntil > now) return true;
  } catch {}

  // 1) Fetch authoritative snapshot
  let snap = null;

  let res;
  try {
    res = await axios.get(
      `${api}/reveal-mode/${key}/state?t=${Date.now()}`,
      { ...authHeaders(), validateStatus: (s) => [200, 202, 204, 410, 404].includes(s) }
    );
  } catch (err) {
    // Hard failures: keep current page and do not bounce UI
    console.warn("[enforceRevealState] fetch error — ignoring", err?.message || err);
    return true;
  }

  // 404 → some environments hit this path incorrectly. Do nothing.
  if (res.status === 404) return true;

  // 204 → transitional/waiting (stage-locked, bridging, etc.). Do not navigate.
  if (res.status === 204) {
    const data = res.data || {};
    const rr = (data?.state && data?.state.kind) ? data.state : null;
    const stageIdx =
      Number.isInteger(data?.stageIndex) ? data.stageIndex :
      (Number.isInteger(rr?.stage) ? rr.stage : null);

    const roundEndAt =
      rr?.roundEndAt ||
      (Number.isFinite(data?.endMs) ? new Date(data.endMs).toISOString() : data?.roundEndAt) ||
      null;

    snap = {
      eventId: key,
      route: "waiting",
      stageIndex: stageIdx,
      matches: [],
      toggleLocked: false,
      lockedIndices: [],
      roundEndAt,
      epoch: data?.epoch ?? rr?.epoch ?? null,
    };
  }

  // 410 → hard-ended, route to portal (unless caller wants to override elsewhere)
  if (res.status === 410) {
    try { sessionStorage.setItem(ROUTE_LOCK_KEY, String(Date.now() + 1000)); } catch {}
    navigate(`/reveal/${key}/portal`, { replace: true, state: { revealExpired: true } });
    return false;
  }

  // 200 → use unified state.kind (fallback to legacy)
  if (res.status === 200) {
    const data = res.data || {};
    const rr   = (data?.state && data?.state.kind) ? data.state : null; // unified
    const legacyState = typeof data?.state === "string" ? data.state : "";
    const kind = rr?.kind || legacyState; // 'match' | 'computing' | 'r1-waiting' | 's2-pending' | 'ended' | ...

    // optional route lock directive from server
    const lockMs = Number(data?.clientDirectives?.routeLockMs || 0);
    if (lockMs > 0) {
      try { sessionStorage.setItem(ROUTE_LOCK_KEY, String(Date.now() + lockMs)); } catch {}
    }

    // derive intended route
   const want = (typeof data?.route === 'string' && data.route) || routeFromKind(kind);

    const stageIdx =
      Number.isInteger(rr?.stage) ? rr.stage :
      (Number.isInteger(data?.stageIndex) ? data.stageIndex : null);

    const roundEndAt =
      rr?.verifyEndAt ||
      rr?.endAt ||
      data?.roundEndAt ||
      (Number.isFinite(data?.endMs) ? new Date(data.endMs).toISOString() : null);

    snap = {
      eventId: key,
      route: want || "waiting",
      stageIndex: stageIdx,
      matches: [],
      toggleLocked: false,
      lockedIndices: [],
      roundEndAt,
      epoch: data?.epoch ?? rr?.epoch ?? null,
    };
  }

  // If nothing could be formed, keep current page.
  if (!snap) return true;

  // 👉 Any time we’re on PRE/PORTAL, nuke sticky keys from prior epoch
  try {
    if (snap.route === "portal") {
      sessionStorage.removeItem(`reveal_partner_sticky_until:${eventId}`);
      sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`);
      sessionStorage.removeItem(`reveal_block_return:${eventId}`);
    }
  } catch {}

  // 2) Publish to subscribers (so pages can pick up stageIndex/toggles/matches)
  _publish(key, snap);

  // 3) Route authority: if server wants a different page, go there.

  // Special case: 'waiting' means "render current page; do not navigate".
  if (snap.route === "waiting") return true;

  const want = String(snap.route || "");
  if (!want) return true;

  // Already on the correct page
  if (want === pageKind) return true;

  // Pass useful state along for the target page to hydrate immediately.
  const idx = Number.isInteger(snap.stageIndex) ? snap.stageIndex : 0;
  const active = Array.isArray(snap.matches) ? snap.matches[idx] : null;

  // Short lock to avoid rubber-banding immediately after navigation
  try { sessionStorage.setItem(ROUTE_LOCK_KEY, String(Date.now() + 1200)); } catch {}

  navigate(`/reveal/${key}/${want}`, {
    replace: true,
    state: {
      stage: Number.isInteger(snap.stageIndex) ? snap.stageIndex : null,
      matchId: active?.matchId || null,
      stageEndsAt: active?.verifyEndAt || snap.roundEndAt || null,
      fromEnforcer: true,
    },
  });

  return false;
}
